import CryptoJS from 'crypto-js';
import { bufferToHex, hexToBuffer } from './helpers';
const pemToBuffer = (pem)=>{
    // Remove PEM headers, \r, \n characters, and spaces
    const cleanBase64 = pem.replace(/-----BEGIN [A-Z ]+-----/, '').replace(/-----END [A-Z ]+-----/, '').replace(/[\r\n\s]/g, '');
    const binaryString = window.atob(cleanBase64);
    const bytes = new Uint8Array(binaryString.length);
    for(let i = 0; i < binaryString.length; i++){
        bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes.buffer;
};
// Automatic conversion of raw PKCS#1 (RSA PUBLIC KEY) to SubjectPublicKeyInfo (SPKI) format
const ensureSpkiFormat = (derBuffer)=>{
    const bytes = new Uint8Array(derBuffer);
    // If the buffer starts with the rsaEncryption OID string (1.2.840.113549.1.1.1),
    // it means that it is already a valid SPKI structure.
    const isAlreadySpki = bytes.length > 15 && bytes[4] === 0x30 && bytes[8] === 0x2a;
    if (isAlreadySpki) {
        return derBuffer;
    }
    // ASN.1 SPKI preamble that matches the PKCS#1 key to the SPKI standard
    const spkiHeader = new Uint8Array([
        0x30,
        0x82,
        0x00,
        0x00,
        0x30,
        0x0d,
        0x06,
        0x09,
        0x2a,
        0x86,
        0x48,
        0x86,
        0xf7,
        0x0d,
        0x01,
        0x01,
        0x01,
        0x05,
        0x00,
        0x03,
        0x82,
        0x00,
        0x00,
        0x00
    ]);
    const totalPkcs1Length = bytes.length;
    const bitStringLength = totalPkcs1Length + 1; // +1 per byte unused bits (0x00)
    const totalSpkiLength = spkiHeader.length - 4 + 1 + bitStringLength;
    spkiHeader[2] = totalSpkiLength >> 8 & 0xff;
    spkiHeader[3] = totalSpkiLength & 0xff;
    spkiHeader[18] = bitStringLength >> 8 & 0xff;
    spkiHeader[19] = bitStringLength & 0xff;
    const result = new Uint8Array(spkiHeader.length + 1 + totalPkcs1Length);
    result.set(spkiHeader, 0);
    result[spkiHeader.length] = 0x00; // Unused bits
    result.set(bytes, spkiHeader.length + 1);
    return result.buffer;
};
// Parsing ASN.1 and extracting parameters
const parseLegacyEncryptedPrivateKeyInfo = (derBuffer)=>{
    const bytes = new Uint8Array(derBuffer);
    // Helper for searching for a sequence of bytes in the buffer
    const findSequence = (pattern, startOffset = 0)=>{
        for(let i = startOffset; i < bytes.length - pattern.length; i++){
            let match = true;
            for(let j = 0; j < pattern.length; j++){
                if (bytes[i + j] !== pattern[j]) {
                    match = false;
                    break;
                }
            }
            if (match) return i;
        }
        return -1;
    };
    // 1. Encrypted Data
    // Last big OCTET STRING block at the end of the file (always takes up the final ~1224B)
    const encryptedData = bytes.slice(bytes.length - 1224);
    // 2. Salt Search (OCTET STRING - 8 bytes)
    // In the PBKDF2 structure, the salt is preceded by the tag 0x04 0x08 (OCTET STRING of length 8)
    let salt = null;
    const saltHeaderIdx = findSequence([
        0x04,
        0x08
    ]);
    if (saltHeaderIdx !== -1 && saltHeaderIdx < 100) {
        salt = bytes.slice(saltHeaderIdx + 2, saltHeaderIdx + 10);
    }
    // 3. IV Search (OCTET STRING - 8 bytes for 3DES or 16 bytes for AES)
    // IV is in the second sequence after salt
    let iv = null;
    if (saltHeaderIdx !== -1) {
        // We are looking for the second OCTET STRING header (0x04 0x08 for 3DES or 0x04 0x10 for AES)
        // after the salt position
        const secondOctetIdx = findSequence([
            0x04,
            0x08
        ], saltHeaderIdx + 10);
        if (secondOctetIdx !== -1 && secondOctetIdx < 200) {
            iv = bytes.slice(secondOctetIdx + 2, secondOctetIdx + 10);
        } else {
            // If 8 bytes are missing, we look for 16 bytes (0x04 0x10)
            const iv16Idx = findSequence([
                0x04,
                0x10
            ], saltHeaderIdx + 10);
            if (iv16Idx !== -1 && iv16Idx < 200) {
                iv = bytes.slice(iv16Idx + 2, iv16Idx + 18);
            }
        }
    }
    // Fallback if tag sequence was modified
    if (!salt) salt = bytes.slice(32, 40);
    if (!iv) iv = bytes.slice(bytes.length - 1224 - 8, bytes.length - 1224);
    return {
        salt,
        iv,
        iterations: 100,
        prf: 'SHA-1',
        encryptedData
    };
};
const parseEncryptedPrivateKeyInfo = (derBuffer)=>{
    const bytes = new Uint8Array(derBuffer);
    // Helper for reading TLV node (Tag-Length-Value)
    const readNode = (offset)=>{
        const tag = bytes[offset];
        let len = bytes[offset + 1];
        let headerLen = 2;
        if (len & 0x80) {
            const numLenBytes = len & 0x7f;
            headerLen = 2 + numLenBytes;
            len = 0;
            for(let i = 0; i < numLenBytes; i++){
                len = len << 8 | bytes[offset + 2 + i];
            }
        }
        return {
            tag,
            headerLen,
            contentOffset: offset + headerLen,
            length: len,
            totalLength: headerLen + len
        };
    };
    try {
        // Top Sequence: EncryptedPrivateKeyInfo
        const topSeq = readNode(0);
        if (topSeq.tag !== 0x30) throw new Error('Missing main header SEQUENCE 0x30');
        // 1. First Child: EncryptionAlgorithmIdentifier (SEQUENCE)
        const algSeq = readNode(topSeq.contentOffset);
        // EncryptedData (OCTET STRING) - second element in topSeq
        const encDataNode = readNode(topSeq.contentOffset + algSeq.totalLength);
        const encryptedData = bytes.slice(encDataNode.contentOffset, encDataNode.contentOffset + encDataNode.length);
        // Inside algSeq: OID (PBES2) + PBES2-params (SEQUENCE)
        let pbes2ParamsOffset = algSeq.contentOffset;
        const firstOid = readNode(pbes2ParamsOffset);
        pbes2ParamsOffset += firstOid.totalLength; // transition behind OID
        const pbes2ParamsSeq = readNode(pbes2ParamsOffset);
        // Inside pbes2ParamsSeq: KeyDerivationFunc (SEQUENCE) + EncryptionScheme (SEQUENCE)
        const kdfSeq = readNode(pbes2ParamsSeq.contentOffset);
        const cipherSeq = readNode(pbes2ParamsSeq.contentOffset + kdfSeq.totalLength);
        // --- A. KDF PARAMS (PBKDF2) ---
        let kdfParamsOffset = kdfSeq.contentOffset;
        const kdfOid = readNode(kdfParamsOffset);
        kdfParamsOffset += kdfOid.totalLength;
        const pbkdf2ParamsSeq = readNode(kdfParamsOffset);
        // Inside pbkdf2ParamsSeq: Salt (OCTET STRING) + Iterations (INTEGER) + optional PRF (SEQUENCE)
        const saltNode = readNode(pbkdf2ParamsSeq.contentOffset);
        const salt = bytes.slice(saltNode.contentOffset, saltNode.contentOffset + saltNode.length);
        const iterNode = readNode(pbkdf2ParamsSeq.contentOffset + saltNode.totalLength);
        let iterations = 100;
        if (iterNode.tag === 0x02) {
            let val = 0;
            for(let i = 0; i < iterNode.length; i++){
                val = val << 8 | bytes[iterNode.contentOffset + i];
            }
            iterations = val;
        }
        // Read PRF (default SHA-1 if missing on node)
        let prf = 'SHA-1';
        const prfOffset = pbkdf2ParamsSeq.contentOffset + saltNode.totalLength + iterNode.totalLength;
        if (prfOffset < pbkdf2ParamsSeq.contentOffset + pbkdf2ParamsSeq.length) {
            const prfSeq = readNode(prfOffset);
            const prfOid = readNode(prfSeq.contentOffset);
            const lastByte = bytes[prfOid.contentOffset + prfOid.length - 1];
            if (lastByte === 0x09) prf = 'SHA-256';
            if (lastByte === 0x0b) prf = 'SHA-512';
        }
        // --- B. CIPHER PARAMS (AES-CBC / TripleDES) ---
        let cipherParamsOffset = cipherSeq.contentOffset;
        const cipherOid = readNode(cipherParamsOffset);
        cipherParamsOffset += cipherOid.totalLength;
        const ivNode = readNode(cipherParamsOffset);
        const iv = bytes.slice(ivNode.contentOffset, ivNode.contentOffset + ivNode.length);
        return {
            salt,
            iv,
            iterations,
            prf,
            encryptedData
        };
    } catch (e) {
        // Nothing goes here at the moment!!!
        console.warn('Głębokie parsowanie struktury PKCS#8 nie powiodło się, używam legacy fallback:', e);
        return parseLegacyEncryptedPrivateKeyInfo(derBuffer);
    // Emergency data snipping for unusual jsrsasign structures
    // return {
    //   salt         : bytes.slice(34, 42),
    //   iv           : bytes.slice(bytes.length - 1224 - 8, bytes.length - 1224),
    //   iterations   : 100,
    //   prf          : 'SHA-1',
    //   encryptedData: bytes.slice(bytes.length - 1224),
    // };
    }
};
/* eslint-disable no-await-in-loop */ const getPbkdf2Hasher = (prf)=>{
    if (prf === 'SHA-256') return CryptoJS.algo.SHA256;
    if (prf === 'SHA-512') return CryptoJS.algo.SHA512;
    return CryptoJS.algo.SHA1;
};
const decryptAndImportPrivateKey = async (parsed, passphrase, hashAlgorithm)=>{
    const iterations = parsed.iterations || 100;
    // List of algorithms to test (AES-256, AES-128, TripleDES)
    const allCiphers = [
        // AES-256-CBC (256-bit key = 8 words)
        {
            name: 'AES-256',
            keyLengthBits: 256,
            algo: CryptoJS.AES,
            ivLen: 16
        },
        // TripleDES (192-bit key = 6 words)
        {
            name: 'TripleDES',
            keySize: 192 / 32,
            algo: CryptoJS.TripleDES,
            ivLen: 8
        },
        // AES-128-CBC (128-bit key = 4 words)
        {
            name: 'AES-128',
            keyLengthBits: 128,
            algo: CryptoJS.AES,
            ivLen: 16
        }
    ];
    const ciphers = allCiphers.filter((c)=>c.ivLen === parsed.iv.length);
    const ivHex = CryptoJS.enc.Hex.parse(bufferToHex(parsed.iv));
    const cipherParams = CryptoJS.lib.CipherParams.create({
        ciphertext: CryptoJS.enc.Hex.parse(bufferToHex(parsed.encryptedData))
    });
    let bytes = null;
    for (const cipher of ciphers){
        try {
            let derivedKey;
            if (cipher.keyLengthBits) {
                // 1a. For new keys (AES): Native PBKDF2 in Web Crypto API
                const encoder = new TextEncoder();
                const passphraseKey = await window.crypto.subtle.importKey('raw', encoder.encode(passphrase), {
                    name: 'PBKDF2'
                }, false, [
                    'deriveKey'
                ]);
                const derivedCryptoKey = await window.crypto.subtle.deriveKey({
                    name: 'PBKDF2',
                    salt: parsed.salt,
                    iterations,
                    hash: [
                        'SHA-512',
                        'SHA-256'
                    ].includes(parsed.prf) ? parsed.prf : 'SHA-1'
                }, passphraseKey, {
                    name: 'AES-CBC',
                    length: cipher.keyLengthBits
                }, true, [
                    'encrypt'
                ]);
                const derivedKeyBuffer = await window.crypto.subtle.exportKey('raw', derivedCryptoKey);
                derivedKey = CryptoJS.enc.Hex.parse(bufferToHex(new Uint8Array(derivedKeyBuffer)));
            } else {
                // 1b. For old keys (TripleDES): CryptoJS Fallback (100 iterations is instant)
                const saltHex = CryptoJS.enc.Hex.parse(bufferToHex(parsed.salt));
                const pbkdf2Hasher = getPbkdf2Hasher(parsed.prf);
                derivedKey = CryptoJS.PBKDF2(passphrase, saltHex, {
                    keySize: cipher.keySize,
                    iterations,
                    hasher: pbkdf2Hasher
                });
            }
            // 2. Decryption with NoPadding (we keep the full, raw ASN.1 data block)
            const decrypted = cipher.algo.decrypt(cipherParams, derivedKey, {
                iv: ivHex,
                mode: CryptoJS.mode.CBC,
                padding: CryptoJS.pad.NoPadding
            });
            const rawHex = decrypted.toString(CryptoJS.enc.Hex);
            if (!rawHex) continue;
            const candidateBytes = hexToBuffer(rawHex);
            // Checks if the header is a valid ASN.1 sequence (0x30)
            // and have valid length byte (0x81 or 0x82)
            if (candidateBytes[0] === 0x30 && (candidateBytes[1] === 0x81 || candidateBytes[1] === 0x82)) {
                bytes = candidateBytes;
                break;
            }
        } catch (e) {
        // Exception ignored, trying another cipher
        }
    }
    if (!bytes) {
        throw new Error('Failed to decrypt key (wrong password or unsupported algorithm).');
    }
    // 3. Trimming PKCS#7 padding based on ASN.1 structure length
    if (bytes[0] === 0x30) {
        let contentLength = 0;
        let headerLen = 2;
        if (bytes[1] === 0x81) {
            contentLength = bytes[2];
            headerLen = 3;
        } else if (bytes[1] === 0x82) {
            contentLength = bytes[2] << 8 | bytes[3];
            headerLen = 4;
        }
        const totalExpectedLength = contentLength + headerLen;
        if (bytes.length >= totalExpectedLength) {
            bytes = bytes.slice(0, totalExpectedLength);
        }
    }
    // 4. Import to Web Crypto API
    const configs = [
        {
            algo: {
                name: 'RSA-OAEP',
                hash: hashAlgorithm
            },
            usages: [
                'decrypt'
            ]
        },
        {
            algo: {
                name: 'RSA-OAEP',
                hash: 'SHA-256'
            },
            usages: [
                'decrypt'
            ]
        },
        {
            algo: {
                name: 'RSA-OAEP',
                hash: 'SHA-1'
            },
            usages: [
                'decrypt'
            ]
        },
        {
            algo: {
                name: 'RSA-OAEP',
                hash: 'SHA-1'
            },
            usages: [
                'decrypt',
                'unwrapKey'
            ]
        },
        {
            algo: {
                name: 'RSASSA-PKCS1-v1_5',
                hash: 'SHA-256'
            },
            usages: [
                'sign'
            ]
        }
    ];
    for (const config of configs){
        try {
            const key = await window.crypto.subtle.importKey('pkcs8', bytes.buffer, config.algo, true, config.usages);
            return key;
        } catch (e) {
        // Exception ignored
        }
    }
    throw new Error('Error importing key into Web Crypto API.');
};
//----------------------------------------------------------------------------------------------------------------------
export const getKeyFromPem = async (pem, passphrase = null, hashAlgorithm = 'SHA-512')=>{
    if (!pem) {
        return null;
    }
    const { subtle } = window.crypto;
    let derBuffer = pemToBuffer(pem);
    if (!passphrase) {
        derBuffer = ensureSpkiFormat(derBuffer);
        return subtle.importKey('spki', derBuffer, {
            name: 'RSA-OAEP',
            hash: hashAlgorithm
        }, true, [
            'encrypt'
        ]);
    }
    const parsed = parseEncryptedPrivateKeyInfo(pemToBuffer(pem));
    return decryptAndImportPrivateKey(parsed, passphrase, hashAlgorithm);
};
